// Pure metric functions for the measurement engine. Everything here is
// deterministic and side-effect free so it can be unit-tested with hand-computed
// fixtures and reused from the Web Worker.
//
// Conventions:
//   * RTT values are milliseconds.
//   * Timestamps are microseconds (client performance.now()*1000).
//   * All *Pct / Wilson bounds returned in Metrics are percentages (0..100).
//   * "loss" means a packet that was sent but never observed back, in any of the
//     three loss classes lostUp / lostDown / unknown. notSent is never loss.

import {
  type LadderRung,
  type Metrics,
  type PacketClass,
  type PacketRecord,
  type RunResult,
  type SweepResult,
  type Tier,
  type Verdict,
  TIER_ORDER,
} from './types';

/** 95% two-sided normal quantile used by the Wilson interval. */
export const WILSON_Z = 1.959963984540054;

/** A single raw observation for one sent (or held-back) sequence number. */
export interface RawSample {
  seq: number;
  /** Client send time in microseconds. Ignored when notSent. */
  sentUs: number;
  /** True when back-pressure held this packet: never counted as loss. */
  notSent: boolean;
  /** Measured RTT in ms if the echo returned, otherwise null. */
  rttMs: number | null;
  /** Node-side serverSeq stamped on the echo, or null if not echoed. */
  serverSeq: number | null;
}

/** The three classes that count as lost. */
const LOSS_CLASSES: ReadonlySet<PacketClass> = new Set<PacketClass>([
  'lostUp',
  'lostDown',
  'unknown',
]);

/** True when a packet class is one of the loss classes. */
export function isLoss(cls: PacketClass): boolean {
  return LOSS_CLASSES.has(cls);
}

/**
 * Classify raw samples into packet records.
 *
 *   ok       = echoed and rtt <= acceptableDelay
 *   late     = echoed and rtt >  acceptableDelay
 *   lostUp   = not echoed and NOT present in the node's receive log
 *   lostDown = not echoed but present in the node's receive log
 *   unknown  = not echoed and no receive log is available
 *   notSent  = held back by back-pressure
 *
 * `nodeReceived` is the set of sequence numbers the node reported receiving, or
 * null when no receive log was delivered. `hasRecvlog` distinguishes "recvlog
 * arrived and this seq was absent" (lostUp) from "no recvlog at all" (unknown).
 * outOfOrder is set when an echo's serverSeq is below that of an earlier (lower
 * seq) echo, i.e. the node received the packets in a different order.
 */
export function classifyPackets(
  samples: readonly RawSample[],
  nodeReceived: ReadonlySet<number> | null,
  hasRecvlog: boolean,
  acceptableDelayMs: number,
): PacketRecord[] {
  const ordered = [...samples].sort((a, b) => a.seq - b.seq);
  let maxServerSeq = -Infinity;
  const out: PacketRecord[] = [];

  for (const s of ordered) {
    let cls: PacketClass;
    let outOfOrder = false;

    if (s.notSent) {
      cls = 'notSent';
    } else if (s.rttMs !== null) {
      cls = s.rttMs <= acceptableDelayMs ? 'ok' : 'late';
      if (s.serverSeq !== null) {
        if (s.serverSeq < maxServerSeq) {
          outOfOrder = true;
        } else {
          maxServerSeq = s.serverSeq;
        }
      }
    } else if (!hasRecvlog || nodeReceived === null) {
      cls = 'unknown';
    } else if (nodeReceived.has(s.seq)) {
      cls = 'lostDown';
    } else {
      cls = 'lostUp';
    }

    out.push({
      seq: s.seq,
      cls,
      sentUs: s.sentUs,
      rttMs: s.rttMs,
      serverSeq: s.serverSeq,
      outOfOrder,
    });
  }
  return out;
}

/**
 * Percentile of a value set using linear interpolation between closest ranks.
 * `p` is in [0, 100]. Returns 0 for an empty input. The input need not be
 * sorted.
 */
export function percentile(values: readonly number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  if (sorted.length === 1) return sorted[0];
  const clamped = Math.min(100, Math.max(0, p));
  const rank = (clamped / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  const frac = rank - lo;
  return sorted[lo] + (sorted[hi] - sorted[lo]) * frac;
}

/** Arithmetic mean, 0 for empty input. */
export function mean(values: readonly number[]): number {
  if (values.length === 0) return 0;
  let sum = 0;
  for (const v of values) sum += v;
  return sum / values.length;
}

/** Population standard deviation, 0 for empty input. */
export function stddev(values: readonly number[]): number {
  if (values.length === 0) return 0;
  const m = mean(values);
  let acc = 0;
  for (const v of values) acc += (v - m) * (v - m);
  return Math.sqrt(acc / values.length);
}

/**
 * Mean of the absolute differences between consecutive values. This is the
 * engine's avgJitter over an RTT series taken in send order.
 */
export function meanAbsConsecutiveDiff(values: readonly number[]): number {
  if (values.length < 2) return 0;
  let acc = 0;
  for (let i = 1; i < values.length; i++) {
    acc += Math.abs(values[i] - values[i - 1]);
  }
  return acc / (values.length - 1);
}

/**
 * RFC 3550 interarrival jitter estimate over an RTT series (ms). Uses the
 * standard exponential smoothing J += (|D| - J)/16 with D the difference of
 * successive transit-time proxies (consecutive RTTs).
 */
export function rfc3550Jitter(rtts: readonly number[]): number {
  let j = 0;
  for (let i = 1; i < rtts.length; i++) {
    const d = rtts[i] - rtts[i - 1];
    j += (Math.abs(d) - j) / 16;
  }
  return j;
}

/** Wilson score interval bounds (fractions 0..1) for `successes` out of `n`. */
export function wilsonInterval(
  successes: number,
  n: number,
  z: number = WILSON_Z,
): { low: number; high: number } {
  if (n <= 0) return { low: 0, high: 0 };
  const phat = successes / n;
  const z2 = z * z;
  const denom = 1 + z2 / n;
  const center = (phat + z2 / (2 * n)) / denom;
  const margin =
    (z * Math.sqrt((phat * (1 - phat)) / n + z2 / (4 * n * n))) / denom;
  return {
    low: Math.max(0, center - margin),
    high: Math.min(1, center + margin),
  };
}

/** Run-length and Gilbert-Elliott statistics for a binary loss sequence. */
export interface BurstStats {
  meanLen: number;
  longest: number;
  fracInBurstsGte3: number;
  ge_p: number;
  ge_q: number;
}

/**
 * Burst statistics over a per-packet loss flag sequence (true = lost). Runs are
 * maximal stretches of consecutive losses. Gilbert-Elliott p (good->bad) and q
 * (bad->good) are estimated from the observed state transitions.
 */
export function burstStats(lossFlags: readonly boolean[]): BurstStats {
  const runLengths: number[] = [];
  let cur = 0;
  let totalLost = 0;
  for (const lost of lossFlags) {
    if (lost) {
      cur++;
      totalLost++;
    } else if (cur > 0) {
      runLengths.push(cur);
      cur = 0;
    }
  }
  if (cur > 0) runLengths.push(cur);

  const longest = runLengths.reduce((m, l) => Math.max(m, l), 0);
  const meanLen = runLengths.length === 0 ? 0 : mean(runLengths);
  const inGte3 = runLengths
    .filter((l) => l >= 3)
    .reduce((a, l) => a + l, 0);
  const fracInBurstsGte3 = totalLost === 0 ? 0 : inGte3 / totalLost;

  // Transition counts for Gilbert-Elliott. State 0 = good, 1 = bad (lost).
  let n00 = 0;
  let n01 = 0;
  let n10 = 0;
  let n11 = 0;
  for (let i = 1; i < lossFlags.length; i++) {
    const prev = lossFlags[i - 1];
    const now = lossFlags[i];
    if (!prev && !now) n00++;
    else if (!prev && now) n01++;
    else if (prev && !now) n10++;
    else n11++;
  }
  const ge_p = n00 + n01 === 0 ? 0 : n01 / (n00 + n01);
  const ge_q = n10 + n11 === 0 ? 0 : n10 / (n10 + n11);

  return { meanLen, longest, fracInBurstsGte3, ge_p, ge_q };
}

/**
 * Slope (ms per second) of the least-squares line fitting RTT against time.
 * Points carry time in seconds and RTT in ms. Returns 0 when the times have no
 * spread (fewer than two distinct x values).
 */
export function driftSlope(
  points: readonly { tSec: number; rttMs: number }[],
): number {
  const n = points.length;
  if (n < 2) return 0;
  let sx = 0;
  let sy = 0;
  for (const pt of points) {
    sx += pt.tSec;
    sy += pt.rttMs;
  }
  const mx = sx / n;
  const my = sy / n;
  let cov = 0;
  let varx = 0;
  for (const pt of points) {
    const dx = pt.tSec - mx;
    cov += dx * (pt.rttMs - my);
    varx += dx * dx;
  }
  if (varx === 0) return 0;
  return cov / varx;
}

/**
 * Total-loss percentage per one-second bucket of the run. Buckets are measured
 * from `startedAtUs`; there are ceil(durationMs/1000) of them. notSent packets
 * are excluded from both numerator and denominator.
 */
export function computeLossPerSecond(
  packets: readonly PacketRecord[],
  startedAtUs: number,
  durationMs: number,
): number[] {
  const buckets = Math.max(1, Math.ceil(durationMs / 1000));
  const lost = new Array<number>(buckets).fill(0);
  const total = new Array<number>(buckets).fill(0);
  for (const p of packets) {
    if (p.cls === 'notSent') continue;
    let idx = Math.floor((p.sentUs - startedAtUs) / 1_000_000);
    if (idx < 0) idx = 0;
    if (idx >= buckets) idx = buckets - 1;
    total[idx]++;
    if (isLoss(p.cls)) lost[idx]++;
  }
  return lost.map((l, i) => (total[i] === 0 ? 0 : (l / total[i]) * 100));
}

/**
 * Compute the full Metrics aggregate from a classified packet list. notSent
 * packets are excluded from the n used as the denominator of every percentage.
 */
export function computeMetrics(packets: readonly PacketRecord[]): Metrics {
  const sent = packets.filter((p) => p.cls !== 'notSent');
  const n = sent.length;

  const echoed = sent.filter((p) => p.cls === 'ok' || p.cls === 'late');
  const rtts = echoed
    .map((p) => p.rttMs)
    .filter((r): r is number => r !== null);

  const counts = {
    lostUp: 0,
    lostDown: 0,
    unknown: 0,
    late: 0,
  };
  for (const p of sent) {
    if (p.cls === 'lostUp') counts.lostUp++;
    else if (p.cls === 'lostDown') counts.lostDown++;
    else if (p.cls === 'unknown') counts.unknown++;
    else if (p.cls === 'late') counts.late++;
  }
  const totalLostCount = counts.lostUp + counts.lostDown + counts.unknown;

  const pct = (x: number): number => (n === 0 ? 0 : (x / n) * 100);

  const wilson = wilsonInterval(totalLostCount, n);

  const lossFlags = sent.map((p) => isLoss(p.cls));

  const driftPoints = echoed
    .filter((p) => p.rttMs !== null)
    .map((p) => ({ tSec: p.sentUs / 1_000_000, rttMs: p.rttMs as number }));

  return {
    n,
    uploadLossPct: pct(counts.lostUp),
    downloadLossPct: pct(counts.lostDown),
    totalLossPct: pct(totalLostCount),
    latePct: pct(counts.late),
    avgLatencyMs: mean(rtts),
    avgJitterMs: meanAbsConsecutiveDiff(rtts),
    rfc3550JitterMs: rfc3550Jitter(rtts),
    p50: percentile(rtts, 50),
    p90: percentile(rtts, 90),
    p99: percentile(rtts, 99),
    minRtt: rtts.length === 0 ? 0 : Math.min(...rtts),
    maxRtt: rtts.length === 0 ? 0 : Math.max(...rtts),
    stddevRtt: stddev(rtts),
    wilsonLossLow: wilson.low * 100,
    wilsonLossHigh: wilson.high * 100,
    burst: burstStats(lossFlags),
    driftMsPerS: driftSlope(driftPoints),
  };
}

/**
 * Build the localisation ladder from a set of runs, one rung per tier present,
 * ordered lan -> isp -> region -> world. When several runs share a tier the one
 * with the most sent packets is chosen (the most statistically solid).
 */
export function buildLadder(runs: readonly RunResult[]): LadderRung[] {
  const byTier = new Map<Tier, RunResult>();
  for (const run of runs) {
    const existing = byTier.get(run.tier);
    if (!existing || run.metrics.n > existing.metrics.n) {
      byTier.set(run.tier, run);
    }
  }
  const rungs: LadderRung[] = [];
  for (const tier of TIER_ORDER) {
    const run = byTier.get(tier);
    if (!run) continue;
    rungs.push({
      tier,
      nodeId: run.nodeId,
      lossPct: run.metrics.totalLossPct,
      latePct: run.metrics.latePct,
      p50: run.metrics.p50,
      p99: run.metrics.p99,
      wilsonLow: run.metrics.wilsonLossLow,
      wilsonHigh: run.metrics.wilsonLossHigh,
    });
  }
  return rungs;
}

/** Human-readable label for a tier used in verdict text. */
function tierLabel(tier: Tier): string {
  switch (tier) {
    case 'lan':
      return 'your local network (LAN)';
    case 'isp':
      return 'your ISP access link';
    case 'region':
      return 'the regional network';
    case 'world':
      return 'the wider internet';
  }
}

/**
 * Detect a message-size threshold above which loss jumps, from a sweep. Returns
 * the smallest size whose total loss exceeds `jumpPct` while a smaller size
 * stayed at or below it, or null when no such MTU-like step exists.
 */
export function detectSizeThreshold(
  sweep: SweepResult,
  jumpPct = 5,
): number | null {
  const bySize = new Map<number, number[]>();
  for (const cell of sweep.cells) {
    const arr = bySize.get(cell.size) ?? [];
    arr.push(cell.run.metrics.totalLossPct);
    bySize.set(cell.size, arr);
  }
  const sizes = [...bySize.keys()].sort((a, b) => a - b);
  let sawLow = false;
  for (const size of sizes) {
    const avg = mean(bySize.get(size) ?? []);
    if (avg <= jumpPct) {
      sawLow = true;
    } else if (sawLow) {
      return size;
    }
  }
  return null;
}

/**
 * Localise the likely fault segment from a set of runs (optionally enriched with
 * a sweep for MTU evidence). The rule: walking the ladder outward, the first
 * tier whose loss Wilson lower bound exceeds the previous tier's Wilson upper
 * bound is the suspect segment. Produces a plain-language verdict plus evidence.
 */
export function localize(
  runs: readonly RunResult[],
  sweep?: SweepResult,
): Verdict {
  const ladder = buildLadder(runs);
  const evidence: string[] = [];

  if (ladder.length === 0) {
    return {
      tier: null,
      text: 'No measurements are available yet, so no segment can be localised.',
      evidence,
    };
  }

  // Find the first outward tier whose loss is significantly worse than the tier
  // before it (non-overlapping 95% Wilson intervals).
  let faultTier: Tier | null = null;
  let faultIndex = -1;
  for (let i = 1; i < ladder.length; i++) {
    if (ladder[i].wilsonLow > ladder[i - 1].wilsonHigh) {
      faultTier = ladder[i].tier;
      faultIndex = i;
      break;
    }
  }

  const runByTier = new Map<Tier, RunResult>();
  for (const rung of ladder) {
    const run = runs.find((r) => r.nodeId === rung.nodeId && r.tier === rung.tier);
    if (run) runByTier.set(rung.tier, run);
  }

  // Directional evidence from the suspect (or worst) tier.
  const focusTier =
    faultTier ??
    ladder.reduce((worst, r) => (r.lossPct > worst.lossPct ? r : worst), ladder[0]).tier;
  const focusRun = runByTier.get(focusTier);
  if (focusRun) {
    const m = focusRun.metrics;
    if (m.uploadLossPct > 0 && m.uploadLossPct >= 2 * Math.max(m.downloadLossPct, 0.001)) {
      evidence.push(
        `Loss is predominantly upstream (${m.uploadLossPct.toFixed(1)}% up vs ${m.downloadLossPct.toFixed(1)}% down).`,
      );
    } else if (m.downloadLossPct > 0 && m.downloadLossPct >= 2 * Math.max(m.uploadLossPct, 0.001)) {
      evidence.push(
        `Loss is predominantly downstream (${m.downloadLossPct.toFixed(1)}% down vs ${m.uploadLossPct.toFixed(1)}% up).`,
      );
    }
    if (m.driftMsPerS > 1) {
      evidence.push(
        `Latency climbs at ${m.driftMsPerS.toFixed(1)} ms/s under load, a classic bufferbloat signature.`,
      );
    }
  }

  if (sweep) {
    const threshold = detectSizeThreshold(sweep);
    if (threshold !== null) {
      evidence.push(
        `Loss appears above ~${threshold} byte messages, pointing at an MTU / fragmentation limit.`,
      );
    }
  }

  if (faultTier && faultIndex >= 0) {
    const prev = ladder[faultIndex - 1];
    const here = ladder[faultIndex];
    const text =
      `Loss steps up at ${tierLabel(faultTier)}: it rises from about ` +
      `${prev.lossPct.toFixed(1)}% at the ${prev.tier} tier to ` +
      `${here.lossPct.toFixed(1)}% here, with non-overlapping confidence ` +
      `intervals. The fault most likely sits on the segment between the ` +
      `${prev.tier} and ${faultTier} tiers.`;
    return { tier: faultTier, text, evidence };
  }

  const worst = ladder.reduce((w, r) => (r.lossPct > w.lossPct ? r : w), ladder[0]);
  if (worst.lossPct < 1) {
    return {
      tier: null,
      text:
        'All tiers show negligible loss and their confidence intervals overlap; ' +
        'no single segment stands out as the fault.',
      evidence,
    };
  }
  return {
    tier: null,
    text:
      `Loss is present (worst at the ${worst.tier} tier, ${worst.lossPct.toFixed(1)}%), ` +
      'but the tiers overlap within confidence, so it cannot be pinned to one ' +
      'segment. Repeat the measurement or lengthen it to tighten the intervals.',
    evidence,
  };
}
